import { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Alert,
  Tooltip,
} from "@mui/material";
import { Download, RefreshCw } from "lucide-react";
import useLogListReducer from "./hooks/useLogList/useLogList";
import useLogDownloadReducer from "./hooks/useLogDownload/useLogDownloadHook";
import useLatestLogsReducer from "./hooks/useLatestLogs/useLatestLogsHook";
import { DEFAULT_DOWNLOAD_FILENAME, DOWNLOAD_RESET_DELAY } from "./constants";
import { formatFileDate, validateDateFormat, extractTimestamp, newLogoIconVisibility } from "./utils";
import newLogIcon from "../../assets/latest_log_logo.png";

const LogManager = ({ serialNumber }) => {
  // State
  const [logFiles, setLogFiles] = useState([]);
  const [fileName, setFileName] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [shouldFetchLatestLogs, setShouldFetchLatestLogs] = useState(false);
  const [fetchStartDate, setFetchStartDate] = useState(null);
  const [fetchEndDate, setFetchEndDate] = useState(null);
  const [showLogRequestInfoMessage, setShowLogRequestInfoMessage] = useState();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const logListKey = serialNumber ? `${serialNumber}_${refreshTrigger}` : null;
  const logList = useLogListReducer(logListKey);
  const logDownloadDetails = useLogDownloadReducer(serialNumber, fileName);
  useLatestLogsReducer(shouldFetchLatestLogs ? serialNumber : null, fetchStartDate, fetchEndDate);

  // Memoized data
  const logListData = useMemo(() => {
    return logList?.state?.data || logList?.state?.status?.data;
  }, [logList?.state?.data, logList?.state?.status?.data]);

  const downloadData = useMemo(() => {
    return logDownloadDetails?.state?.data;
  }, [logDownloadDetails?.state?.data]);

  // Download handler
  const triggerDownload = useCallback((presignedUrl, fileNameToDownload) => {
    const link = document.createElement("a");
    link.href = presignedUrl;
    link.download = fileNameToDownload || DEFAULT_DOWNLOAD_FILENAME;
    link.rel = "noopener noreferrer";
    link.target = "_blank";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  useEffect(() => {
    if (!downloadData || !fileName) return;

    if (typeof downloadData === "string" && downloadData.trim()) {
      triggerDownload(downloadData, fileName);
      // Reset fileName after download to allow re-download
      setTimeout(() => setFileName(null), DOWNLOAD_RESET_DELAY);
    }
  }, [downloadData, fileName, triggerDownload]);

  // sorting logic
  useEffect(() => {
    if (!Array.isArray(logListData) || logListData.length === 0) {
      setLogFiles([]);
      return;
    }

    const sortedAndLimited = [...logListData].sort((a, b) => extractTimestamp(b) - extractTimestamp(a)).slice(0, 10);

    setLogFiles(sortedAndLimited);
  }, [logListData]);

  // Generic date change handler
  const handleDateChange = useCallback(
    field => e => {
      const value = e.target.value;
      const setDate = field === "start" ? setStartDate : setEndDate;
      const setError = field === "start" ? setStartDateError : setEndDateError;

      setDate(value);

      if (value.trim() === "") {
        setError("");
      } else if (!validateDateFormat(value)) {
        setError("Date must be in YYYY-MM-DD format (e.g., 2025-12-02)");
      } else {
        setError("");
      }
    },
    []
  );

  // Validate if both dates are filled and valid
  const isFetchEnabled = useMemo(() => {
    const dates = [
      { value: startDate, error: startDateError },
      { value: endDate, error: endDateError },
    ];

    return dates.every(({ value, error }) => value.trim() !== "" && !error && validateDateFormat(value));
  }, [startDate, endDate, startDateError, endDateError]);

  const handleFetchClick = () => {
    if (isFetchEnabled) {
      // Set the dates to trigger the API call via the hook
      setFetchStartDate(startDate);
      setFetchEndDate(endDate);
      setShouldFetchLatestLogs(true);
      setShowLogRequestInfoMessage(true);
      setTimeout(() => {
        setShowLogRequestInfoMessage(false);
      }, 15000);
      setTimeout(() => {
        setFetchStartDate("");
        setFetchEndDate("");
      }, 0);
      setStartDate("");
      setEndDate("");
    }
  };

  const handleDownload = fileName => {
    setFileName(fileName);
  };

  const handleRefresh = () => {
    // Increment refresh trigger to force API call
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Fetch Button and Date Inputs */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            {showLogRequestInfoMessage && (
              <Alert
                variant="outlined"
                severity="info"
                sx={{
                  minHeight: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#fff",
                  color: "#6b7280",
                  borderColor: "rgba(0, 0, 0, 0.23)",
                  padding: "8px 12px",
                  "& .MuiAlert-icon": {
                    color: "#6b7280",
                    marginRight: "8px",
                  },
                  "& .MuiAlert-message": {
                    color: "#6b7280",
                    fontSize: "0.875rem",
                    padding: 0,
                    lineHeight: 1.5,
                  },
                }}
              >
                Fetching latest logs might take up to 5 minutes
              </Alert>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
            <TextField
              label="Start Date"
              placeholder="YYYY-MM-DD"
              value={startDate}
              onChange={handleDateChange("start")}
              error={!!startDateError}
              size="small"
              sx={{ width: "180px", height: "40px" }}
              inputProps={{
                maxLength: 10,
              }}
            />

            <TextField
              label="End Date"
              placeholder="YYYY-MM-DD"
              value={endDate}
              onChange={handleDateChange("end")}
              error={!!endDateError}
              size="small"
              sx={{ width: "180px", height: "40px" }}
              inputProps={{
                maxLength: 10,
              }}
            />

            <Button
              variant="outlined"
              onClick={handleFetchClick}
              disabled={!isFetchEnabled}
              size="small"
              sx={{
                width: "180px",
                height: "40px",
                backgroundColor: "#fff",
                color: "rgba(0, 0, 0, 0.87)",
                borderColor: "rgba(0, 0, 0, 0.23)",
                borderRadius: "4px",
                textTransform: "none",
                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                "&:hover": {
                  backgroundColor: "#f9fafb",
                  borderColor: "rgba(0, 0, 0, 0.87)",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                },
                "&:disabled": {
                  backgroundColor: "#fff",
                  color: "rgba(0, 0, 0, 0.26)",
                  borderColor: "rgba(0, 0, 0, 0.12)",
                  boxShadow: "none",
                },
              }}
            >
              Request Charger Logs
            </Button>
            <Tooltip title="Refresh logs">
              <IconButton
                onClick={handleRefresh}
                disabled={!serialNumber}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "transparent",
                  color: "rgba(0, 0, 0, 0.87)",
                }}
              >
                <RefreshCw size={20} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Log Files Table */}
      <TableContainer
        component={Paper}
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f9fafb" }}>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "100px" }} align="center">
                Download
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logFiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: "#6b7280" }}>
                  {serialNumber?.length ? "No log files available." : "Please select a charger to view log files."}
                </TableCell>
              </TableRow>
            ) : (
              logFiles.map((file, index) => (
                <TableRow
                  key={file}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f9fafb",
                    },
                  }}
                >
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>{formatFileDate(file) || "—"}</span>

                      {index === 0 && newLogoIconVisibility(file) && (
                        <img src={newLogIcon} alt="new log" width={25} height={25} style={{ display: "block" }} />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{file || `Log File ${index + 1}`}</TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => handleDownload(file)} sx={{ color: "#1976d2" }} size="small">
                      <Download size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

LogManager.propTypes = {
  serialNumber: PropTypes.string,
};

export default LogManager;
